import type { ApiKeys } from '@/types/api'

export const useRecaptcha = () => {
  const passThrowRecaptchaV3 = useState('passThrowRecaptchaV3', () => true)
  const recaptchaLoaded = useState('recaptchaLoaded', () => false)
  const recaptchaV2ApiKey = useState<string | null>('recaptchaV2ApiKey', () => null)
  const recaptchaV3ApiKey = useState<string | null>('recaptchaV3ApiKey', () => null)
  const recaptchaV2Key = useState<string | undefined>('recaptchaV2Key')
  const apiKeysPromise = useState<Promise<void | ApiKeys> | null>('api-keys', () => null)

  const nuxtApp = useNuxtApp()

  const getRecaptchaToken = async (recaptchaActionName: string) => {
    const { public: config } = useRuntimeConfig()
    const apiBaseUrl = config.apiBaseUrl ? config.apiBaseUrl : '$NUXT_PUBLIC_API_BASE_URL'
    if (!apiKeysPromise.value && (!recaptchaV3ApiKey.value || !!recaptchaV2ApiKey.value)) {
      const getRecaptchaApi3Key = new Promise((resolve) => {
        apiKeysPromise.value = $fetch<ApiKeys>(`${apiBaseUrl}/public/settings/api-keys`).then(
          ({ data }) => {
            recaptchaV2ApiKey.value = data.recaptcha_v2_site_key.value
            recaptchaV3ApiKey.value = data.recaptcha_site_key.value
            resolve(data)
          }
        )
      })
      await getRecaptchaApi3Key
    }

    if (recaptchaV3ApiKey.value) {
      await load(recaptchaV3ApiKey.value)
    } else {
      if (!recaptchaV3ApiKey.value) {
        console.warn('No Google Recaptcha v3 site key provided')
      }
      if (!recaptchaV2ApiKey.value) {
        console.warn('No Google Recaptcha v2 site key provided')
      }
    }
    const token = recaptchaV2Key.value ?? ((await executeV3(recaptchaActionName)) as string)
    const versionName = recaptchaV2Key.value
      ? ('X-Google-Recaptcha-V2-Token' as const)
      : ('X-Google-Recaptcha-V3-Token' as const)

    return {
      versionName,
      token,
    }
  }

  const load = async (v3ApiKey: string) => {
    await new Promise((resolve) => {
      nuxtApp.runWithContext(async () => {
        const { load, onLoaded } = useScript(
          {
            src: `https://www.google.com/recaptcha/api.js?render=${v3ApiKey}`,
            referrerpolicy: false,
            crossorigin: false,
          },
          { trigger: 'manual' }
        )

        onLoaded(() => {
          recaptchaLoaded.value = true
        })
        await load()
        resolve(true)
      })
    })
  }

  const executeV3 = async (action: string) => {
    if (!recaptchaV3ApiKey.value) {
      throw Error('Missing Recaptcha API KEY v3')
    }
    if (!recaptchaLoaded.value) {
      throw Error('Recaptcha is not yet loaded')
    }
    return new Promise((rosolve, reject) => {
      try {
        grecaptcha.ready(function () {
          grecaptcha.execute(recaptchaV3ApiKey.value, { action }).then(function (token: string) {
            rosolve(token)
          })
        })
      } catch (e) {
        console.error(e)
        reject(e)
      }
    })
  }

  const resetReCaptcha = () => {
    passThrowRecaptchaV3.value = true
    recaptchaV2Key.value = undefined
  }

  watch(recaptchaV2Key, (newValue) => {
    if (newValue) {
      passThrowRecaptchaV3.value = true
    }
  })

  const callbacks: (() => void | Promise<void>)[] = []

  watch(recaptchaV2Key, async (newValue) => {
    if (newValue) {
      for (let i = 0; i < callbacks.length; i++) {
        await callbacks[i]!()
      }
    }
  })

  const onPassThrowReCaptchaV2 = (cb: () => void | Promise<void>) => {
    callbacks.push(cb)
  }

  return {
    load,
    executeV3,
    recaptchaLoaded,
    passThrowRecaptchaV3,
    recaptchaV2ApiKey,
    recaptchaV3ApiKey,
    recaptchaV2Key,
    apiKeysPromise,
    getRecaptchaToken,
    resetReCaptcha,
    onPassThrowReCaptchaV2,
  }
}
